import {
  DevelopmentPathCycleStateDto,
  DevelopmentPlanStatusDto,
  EmployeeProfileRoleDto,
} from '@/api/generated';
import { SectionTranslationModel } from '@/assets/locales/views/employeeProfile/sections/SectionTranslation.model.ts';

export type EmployeeProfileDevelopmentChatFormSectionTranslation = SectionTranslationModel<{
  chat: {
    actions: {
      completeMilestone: string;
      deleteMilestone: string;
      editMilestone: string;
    };
    backToPaths: string;
    confirmSending: string;
    content: {
      form: {
        enterMessage: string;
        fastQuestions: string;
        streamingError: string;
        submitButton: string;
      };
      noMessages: string;
      shortcuts: {
        suggestedPrompt: {
          buttonText: string;
          onClickText: string;
        };
        updateMilestones: {
          buttonText: string;
          onClickText: string;
        };
      };
    };
    developmentDirection: string;
    developmentPathInfo: string;
    editModeLabel: string;
    editModeWarning: string;
    error: {
      notInEditMode: string;
      stepsRequirement: string;
    };
    headerDescription: string;
    headerTitle: string;
    leaveChatConfirmation: string;
    noDevelopmentPath: string;
    planPreview: string;
    sendToSupervisor: string;
    welcomeMessage: string;
  };
  competencyDevelopment: {
    actions: string;
    completed: string;
    completedAt: string;
    milestone: string;
    step: string;
  };
  cycleSelection: {
    askSupervisor: string;
    createFirstCycle: string;
    createPathsCyclesForm: {
      cancelButton: string;
      developmentDirection: string;
      numberOfMonths: string;
      placeholder: string;
      submitButton: string;
      submitEditButton: string;
      validFrom: string;
      validTo: string;
    };
    cycle: string;
    noDevelopmentCycles: string;
    selectDevelopmentCycle: string;
  };
  developmentPath: {
    acceptanceForm: {
      acceptPlan: string;
      cancel: string;
      comment: string;
      completeBlockedReason: string;
      confirmReject: string;
      rejectPlan: string;
      submitBlockedReason: string;
    };
    changeState: {
      [DevelopmentPathCycleStateDto.Cancelled]: string;
      [DevelopmentPathCycleStateDto.Completed]: string;
      [DevelopmentPathCycleStateDto.Draft]: string;
      [DevelopmentPathCycleStateDto.ReadyForRealization]: string;
      [DevelopmentPathCycleStateDto.SupervisorApproved]: string;
      [DevelopmentPathCycleStateDto.SupervisorRejected]: string;
    };
    chatNotAvailable: string;
    chatNotAvailableReasonRole: string;
    chatNotAvailableReasonState: string;
    continueChat: string;
    createdBy: string;
    currentStatus: string;
    developmentDirection: string;
    duration: string;
    editPath: string;
    history: {
      by: string;
      noHistory: string;
      title: string;
    };
    months: string;
    preview: string;
    startChat: string;
    title: string;
  };
  infoChip: {
    [EmployeeProfileRoleDto.Admin]: {
      [DevelopmentPlanStatusDto.ReadyForRealization]: string;
    };
    [EmployeeProfileRoleDto.Owner]: {
      [DevelopmentPlanStatusDto.SupervisorApproved]: string;
      [DevelopmentPlanStatusDto.SupervisorRejected]: string;
    };
    [EmployeeProfileRoleDto.P1]: {
      [DevelopmentPlanStatusDto.ReadyForRealization]: string;
    };
    noState: string;
  };
  pathPreview: {
    changeState: {
      pathCompleted: string;
      pathFinished: string;
      pathInProgress: string;
    };
    markAsDone: string;
    markAsUndone: string;
    milestonesActions: string;
    milestonesCount: string;
    milestonesProgress: string;
    pathRejected: string;
    previewTitle: string;
    status: {
      inProgress: string;
      notStarted: string;
    };
    timelineMarkers: {
      meetings: {
        mdrFeedback: string;
        pmcSummary: string;
        sopSummary: string;
      };
      months: {
        april: string;
        august: string;
        january: string;
      };
    };
  };
}>;

export const employeeProfileDevelopmentChatFormSectionTranslation: EmployeeProfileDevelopmentChatFormSectionTranslation =
  {
    content: {
      chat: {
        actions: {
          completeMilestone: 'MARK AS COMPLETED: {{milestone}}',
          deleteMilestone: 'DELETE: {{milestone}}',
          editMilestone: 'EDIT: {{milestone}}, according to recommendations:',
        },
        backToPaths: 'Save plan and exit',
        confirmSending:
          'Are you sure you want to send the plan to your supervisor? You will not be able to edit it until the supervisor reviews it.',
        content: {
          form: {
            enterMessage: 'Write a message and press send',
            fastQuestions: 'Shortcuts',
            streamingError: 'An error occurred, please try again later',
            submitButton: 'Start',
          },
          noMessages:
            'Hi, you are starting to create a development plan. Your plan should include 2 to 5 steps that will help you achieve your goal. I will assist you in this process.',
          shortcuts: {
            suggestedPrompt: {
              buttonText: 'Starting prompt',
              onClickText:
                "I'm looking for support in preparing a professional development plan. I want to approach this strategically - identify goals, determine necessary competencies, and create an action timeline. Can you be my guide through this process?",
            },
            updateMilestones: {
              buttonText: 'Update plan',
              onClickText: 'UPDATE plan according to chat content',
            },
          },
        },
        developmentDirection: 'Defined development direction',
        developmentPathInfo: 'Development Plan',
        editModeLabel: 'Edit Mode',
        editModeWarning:
          'By enabling edit mode, you can modify the development plan. This will undo the plan completion and notify the supervisor.',
        error: {
          notInEditMode: 'You can only update the plan in edit mode.',
          stepsRequirement: 'Your plan must have at least 2 steps to send it to your supervisor.',
        },
        headerDescription: 'Helping you develop in direction: {{developmentDirection}}',
        headerTitle: 'Development Plan Assistant',
        leaveChatConfirmation:
          'Are you sure you want to leave the chat? The chat history will be lost.',
        noDevelopmentPath: 'No detailed development direction found. Create one to get started.',
        planPreview: 'Plan Preview',
        sendToSupervisor: 'Send plan to Supervisor',
        welcomeMessage:
          'Hi! I am here to help you create your development plan. How can I assist you today?',
      },
      competencyDevelopment: {
        actions: 'Actions',
        completed: 'Completed',
        completedAt: 'Completed At',
        milestone: 'Milestone',
        step: 'Step',
      },
      cycleSelection: {
        askSupervisor: 'Ask your supervisor to create a development direction for you.',
        createFirstCycle: 'Create your first development path to start the chat.',
        createPathsCyclesForm: {
          cancelButton: 'Cancel',
          developmentDirection: 'Development Direction',
          numberOfMonths: 'Number of Months',
          placeholder: 'Enter the development plan direction',
          submitButton: 'Save direction',
          submitEditButton: 'Save Changes',
          validFrom: 'Valid from',
          validTo: 'Valid to',
        },
        cycle: 'Path',
        noDevelopmentCycles: 'No Development Paths',
        selectDevelopmentCycle: 'Select Development Path',
      },
      developmentPath: {
        acceptanceForm: {
          acceptPlan: 'Accept the plan',
          cancel: 'Cancel',
          comment: 'Comment',
          completeBlockedReason: 'Complete all steps to finish the plan',
          confirmReject: 'Confirm rejection',
          rejectPlan: 'Reject the plan',
          submitBlockedReason: 'The plan needs at least 2 development paths',
        },
        changeState: {
          [DevelopmentPathCycleStateDto.Cancelled]: 'Cancel',
          [DevelopmentPathCycleStateDto.Completed]: 'Complete',
          [DevelopmentPathCycleStateDto.Draft]: 'Revert to draft',
          [DevelopmentPathCycleStateDto.ReadyForRealization]: 'In realization',
          [DevelopmentPathCycleStateDto.SupervisorApproved]: 'Approve',
          [DevelopmentPathCycleStateDto.SupervisorRejected]: 'Reject',
        },
        chatNotAvailable: 'Chat is not available right now due to: {{reason}}',
        chatNotAvailableReasonRole: 'Your role does not allow to edit this path.',
        chatNotAvailableReasonState: 'The current plan status does not allow editing.',
        continueChat: 'Edit Individual Development Plan',
        createdBy: 'Created By',
        currentStatus: 'Current Status',
        developmentDirection: 'Development Direction',
        duration: 'Duration',
        editPath: 'Edit Development Direction',
        history: {
          by: 'By',
          noHistory: 'No history available.',
          title: 'History',
        },
        months: 'months',
        preview: 'Development Plan',
        startChat: 'Create Individual Development Plan',
        title: 'Development Plan Status',
      },
      infoChip: {
        [EmployeeProfileRoleDto.Admin]: {
          [DevelopmentPlanStatusDto.ReadyForRealization]: 'Approve the development path',
        },
        [EmployeeProfileRoleDto.Owner]: {
          [DevelopmentPlanStatusDto.SupervisorApproved]: 'Start executing the development plan',
          [DevelopmentPlanStatusDto.SupervisorRejected]:
            'Improve the development plan based on supervisor’s comment',
        },
        [EmployeeProfileRoleDto.P1]: {
          [DevelopmentPlanStatusDto.ReadyForRealization]: 'Approve the development path',
        },
        noState: 'Start working on the development plan',
      },
      pathPreview: {
        changeState: {
          pathCompleted: 'Finish path',
          pathFinished: 'Path has been completed!',
          pathInProgress: 'Get started',
        },
        markAsDone: 'Mark as completed',
        markAsUndone: 'Mark as undone',
        milestonesActions: 'Actions:',
        milestonesCount: '{{completedMilestones}} out of {{totalMilestones}} completed',
        milestonesProgress: 'Milestones completion progress',
        pathRejected:
          'The path has been rejected by the supervisor. Please correct the errors according to the comments and resubmit for approval.',
        previewTitle: 'Development Plan Preview',
        status: {
          inProgress: 'DEVELOPMENT PLAN IN PROGRESS',
          notStarted: 'DEVELOPMENT PLAN NOT STARTED YET',
        },
        timelineMarkers: {
          meetings: {
            mdrFeedback: 'MDR Feedback Meeting',
            pmcSummary: 'PMC Summary Meeting',
            sopSummary: 'SOP Summary Meeting',
          },
          months: {
            april: 'APRIL',
            august: 'AUGUST',
            january: 'JANUARY',
          },
        },
      },
    },
    headerTitle: 'Development Plan Creation',
    headerTitleShorter: 'Development Plan Creation',
  };
